'use client'

import { useEffect, useMemo, useRef, useState } from 'react'

import { type TMycleContentMenu, type TMycleContentSeriesCode } from './home'
import {
  useMycleContentCategoriesQuery,
  useMycleContentsBySeriesQuery,
  useMycleContentsInfiniteQuery,
} from '../api/query'
import { mapTopicSortToServerSort, type TTopicSort } from '../lib/topicContents'

interface IUseCarStoryTabPageParams {
  /** 콘텐츠 메뉴 (CAR / DRIVE / NOW 등) */
  menu: TMycleContentMenu
  /** 상단 큐레이팅 시리즈 코드 */
  seriesCode: TMycleContentSeriesCode
  /** 마운트 1회 PV 트래킹 */
  onPageView: () => void
}

/**
 * 차량이야기 탭 페이지(DrivePage/MaintenancePage)의 공통 골격 훅.
 *
 * 책임:
 * - sort/category 상태
 * - 3개 쿼리 (큐레이팅 시리즈, 카테고리, 콘텐츠 목록)
 * - 첫 진입 PV 트래킹
 *
 * 호출부는 데이터/콜백을 받아 UI 렌더만 담당하면 됨.
 */
const useCarStoryTabPage = ({
  menu,
  seriesCode,
  onPageView,
}: IUseCarStoryTabPageParams) => {
  const [selectedSort, setSelectedSort] = useState<TTopicSort>('최신순')
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  )

  const {
    data: seriesData,
    isLoading: isSeriesLoading,
    isError: isSeriesError,
    refetch: refetchSeries,
  } = useMycleContentsBySeriesQuery({ code: seriesCode })

  const {
    data: categoriesData,
    isLoading: isCategoriesLoading,
    isError: isCategoriesError,
    refetch: refetchCategories,
  } = useMycleContentCategoriesQuery({ menu })

  const {
    data: contentsData,
    isLoading: isContentsLoading,
    // 인피니트 쿼리의 isError는 fetchNextPage 실패도 포함됨 → 첫 페이지 실패만 잡기 위해 isLoadingError 사용.
    isLoadingError: isContentsError,
    refetch: refetchContents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  } = useMycleContentsInfiniteQuery({
    menu,
    sort: mapTopicSortToServerSort(selectedSort),
    categoryId: selectedCategoryId ?? undefined,
  })

  /** 모든 페이지 콘텐츠를 평탄화한 목록 */
  const contentsItems = useMemo(
    () => contentsData?.pages.flatMap(page => page.content) ?? [],
    [contentsData]
  )

  const hasTrackedRef = useRef(false)
  useEffect(() => {
    if (hasTrackedRef.current) return
    hasTrackedRef.current = true
    onPageView()
    // onPageView는 호출 시점 안정성을 가정 — mount 1회만 호출
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    // 상태
    selectedSort,
    setSelectedSort,
    selectedCategoryId,
    setSelectedCategoryId,
    // 쿼리 데이터
    seriesData,
    categoriesData,
    contentsItems,
    // 로딩/에러 (상단 영역: series + categories)
    isLoading: isSeriesLoading || isCategoriesLoading,
    isError: isSeriesError || isCategoriesError,
    retry: () => {
      refetchSeries()
      refetchCategories()
    },
    // 콘텐츠 리스트 영역 (별도 로딩/에러 + 인피니트)
    isContentsLoading,
    isContentsError,
    refetchContents,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
  }
}

export default useCarStoryTabPage
